import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  BarChart3,
  TrendingUp,
  Mail,
  CheckCircle,
  Eye,
  MousePointerClick,
  XCircle,
  Download,
} from 'lucide-react';
import { EmailTemplateConfig, TemplateAnalytics } from '@/services/emailTemplateConfigService';

interface TemplateAnalyticsPanelProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  template: EmailTemplateConfig | null;
  analytics: TemplateAnalytics | undefined;
  allAnalytics: TemplateAnalytics[];
}

export const TemplateAnalyticsPanel = ({
  open,
  onOpenChange,
  template,
  analytics,
  allAnalytics,
}: TemplateAnalyticsPanelProps) => {
  const [selectedPeriod, setSelectedPeriod] = useState<'all' | '7d' | '30d' | '90d'>('all');

  if (!template) return null;

  const stats = analytics || {
    template_key: template.template_key,
    total_sent: 0,
    total_delivered: 0,
    total_opened: 0,
    total_clicked: 0,
    delivery_rate: 0,
    open_rate: 0,
    click_rate: 0,
  };

  const failedCount = stats.total_sent - stats.total_delivered;
  const failureRate = stats.total_sent > 0 ? ((failedCount / stats.total_sent) * 100) : 0;

  // Calculate engagement score (weighted average)
  const engagementScore = stats.total_sent > 0
    ? (stats.delivery_rate * 0.3 + stats.open_rate * 0.4 + stats.click_rate * 0.3)
    : 0;

  // Get ranking among all templates
  const sortedByEngagement = [...allAnalytics].sort((a, b) => {
    const scoreA = a.delivery_rate * 0.3 + a.open_rate * 0.4 + a.click_rate * 0.3;
    const scoreB = b.delivery_rate * 0.3 + b.open_rate * 0.4 + b.click_rate * 0.3;
    return scoreB - scoreA;
  });
  const ranking = sortedByEngagement.findIndex(a => a.template_key === template.template_key) + 1;

  const getScoreColor = (score: number) => {
    if (score >= 70) return 'text-green-600';
    if (score >= 50) return 'text-blue-600';
    if (score >= 30) return 'text-orange-600';
    return 'text-red-600';
  };

  const getScoreBadge = (score: number) => {
    if (score >= 70) return { label: 'Excellent', variant: 'default' as const };
    if (score >= 50) return { label: 'Good', variant: 'secondary' as const };
    if (score >= 30) return { label: 'Fair', variant: 'outline' as const };
    return { label: 'Poor', variant: 'destructive' as const };
  };

  const scoreBadge = getScoreBadge(engagementScore);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <BarChart3 className="h-5 w-5" />
            Template Analytics: {template.template_name}
          </DialogTitle>
          <DialogDescription>
            Detailed performance metrics and insights for this email template
          </DialogDescription>
        </DialogHeader>

        <Tabs defaultValue="overview" className="w-full">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="performance">Performance</TabsTrigger>
            <TabsTrigger value="insights">Insights</TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-4 mt-4">
            {/* Key Metrics Cards */}
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Total Sent</CardTitle>
                  <Mail className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{stats.total_sent.toLocaleString()}</div>
                  <p className="text-xs text-muted-foreground mt-1">
                    All-time emails sent
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Delivered</CardTitle>
                  <CheckCircle className="h-4 w-4 text-green-600" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-green-600">
                    {stats.total_delivered.toLocaleString()}
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    {stats.delivery_rate.toFixed(1)}% delivery rate
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Opened</CardTitle>
                  <Eye className="h-4 w-4 text-blue-600" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-blue-600">
                    {stats.total_opened.toLocaleString()}
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    {stats.open_rate.toFixed(1)}% open rate
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Clicked</CardTitle>
                  <MousePointerClick className="h-4 w-4 text-purple-600" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-purple-600">
                    {stats.total_clicked.toLocaleString()}
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    {stats.click_rate.toFixed(1)}% click rate
                  </p>
                </CardContent>
              </Card>
            </div>

            {/* Engagement Score */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <TrendingUp className="h-5 w-5" />
                  Engagement Score
                </CardTitle>
                <CardDescription>
                  Overall performance metric based on delivery, open, and click rates
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between">
                  <div>
                    <div className={`text-4xl font-bold ${getScoreColor(engagementScore)}`}>
                      {engagementScore.toFixed(1)}
                    </div>
                    <div className="flex items-center gap-2 mt-2">
                      <Badge variant={scoreBadge.variant}>{scoreBadge.label}</Badge>
                      {allAnalytics.length > 1 && (
                        <span className="text-sm text-muted-foreground">
                          #{ranking} of {allAnalytics.length} templates
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="space-y-2 text-right">
                    <div className="text-sm">
                      <span className="text-muted-foreground">Delivery: </span>
                      <span className="font-medium">{stats.delivery_rate.toFixed(1)}%</span>
                    </div>
                    <div className="text-sm">
                      <span className="text-muted-foreground">Open: </span>
                      <span className="font-medium">{stats.open_rate.toFixed(1)}%</span>
                    </div>
                    <div className="text-sm">
                      <span className="text-muted-foreground">Click: </span>
                      <span className="font-medium">{stats.click_rate.toFixed(1)}%</span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Template Info */}
            <Card>
              <CardHeader>
                <CardTitle>Template Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-muted-foreground">Template Key</p>
                    <p className="font-medium">{template.template_key}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Category</p>
                    <Badge variant="outline">{template.category}</Badge>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Status</p>
                    <Badge variant={template.enabled ? 'default' : 'secondary'}>
                      {template.enabled ? 'Active' : 'Disabled'}
                    </Badge>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">From</p>
                    <p className="font-medium text-sm">
                      {template.from_name} &lt;{template.from_email}&gt;
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Performance Tab */}
          <TabsContent value="performance" className="space-y-4 mt-4">
            <div className="grid gap-4 md:grid-cols-2">
              {/* Delivery Performance */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Delivery Performance</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Successful Deliveries</span>
                      <span className="font-medium">{stats.total_delivered}</span>
                    </div>
                    <div className="w-full bg-muted rounded-full h-2">
                      <div
                        className="bg-green-600 h-2 rounded-full transition-all"
                        style={{ width: `${stats.delivery_rate}%` }}
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Failed Deliveries</span>
                      <span className="font-medium">{failedCount}</span>
                    </div>
                    <div className="w-full bg-muted rounded-full h-2">
                      <div
                        className="bg-red-600 h-2 rounded-full transition-all"
                        style={{ width: `${failureRate}%` }}
                      />
                    </div>
                  </div>

                  <div className="pt-2 border-t">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">Overall Rate</span>
                      <span className="text-lg font-bold text-green-600">
                        {stats.delivery_rate.toFixed(1)}%
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Engagement Performance */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Engagement Performance</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Email Opens</span>
                      <span className="font-medium">{stats.total_opened}</span>
                    </div>
                    <div className="w-full bg-muted rounded-full h-2">
                      <div
                        className="bg-blue-600 h-2 rounded-full transition-all"
                        style={{ width: `${stats.open_rate}%` }}
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Link Clicks</span>
                      <span className="font-medium">{stats.total_clicked}</span>
                    </div>
                    <div className="w-full bg-muted rounded-full h-2">
                      <div
                        className="bg-purple-600 h-2 rounded-full transition-all"
                        style={{ width: `${stats.click_rate}%` }}
                      />
                    </div>
                  </div>

                  <div className="pt-2 border-t">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">Click-to-Open Rate</span>
                      <span className="text-lg font-bold text-purple-600">
                        {stats.total_opened > 0
                          ? ((stats.total_clicked / stats.total_opened) * 100).toFixed(1)
                          : '0.0'}%
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Detailed Metrics */}
            <Card>
              <CardHeader>
                <CardTitle>Detailed Metrics</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex justify-between items-center py-2 border-b">
                    <span className="text-sm text-muted-foreground">Total Emails Sent</span>
                    <span className="font-medium">{stats.total_sent.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b">
                    <span className="text-sm text-muted-foreground">Successfully Delivered</span>
                    <span className="font-medium text-green-600">
                      {stats.total_delivered.toLocaleString()} ({stats.delivery_rate.toFixed(2)}%)
                    </span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b">
                    <span className="text-sm text-muted-foreground">Failed Deliveries</span>
                    <span className="font-medium text-red-600">
                      {failedCount.toLocaleString()} ({failureRate.toFixed(2)}%)
                    </span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b">
                    <span className="text-sm text-muted-foreground">Unique Opens</span>
                    <span className="font-medium text-blue-600">
                      {stats.total_opened.toLocaleString()} ({stats.open_rate.toFixed(2)}%)
                    </span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b">
                    <span className="text-sm text-muted-foreground">Unique Clicks</span>
                    <span className="font-medium text-purple-600">
                      {stats.total_clicked.toLocaleString()} ({stats.click_rate.toFixed(2)}%)
                    </span>
                  </div>
                  <div className="flex justify-between items-center py-2">
                    <span className="text-sm text-muted-foreground">Click-to-Open Rate</span>
                    <span className="font-medium">
                      {stats.total_opened > 0
                        ? ((stats.total_clicked / stats.total_opened) * 100).toFixed(2)
                        : '0.00'}%
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Insights Tab */}
          <TabsContent value="insights" className="space-y-4 mt-4">
            <Card>
              <CardHeader>
                <CardTitle>Performance Insights</CardTitle>
                <CardDescription>
                  AI-powered recommendations to improve your email performance
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Delivery Insights */}
                {stats.delivery_rate < 95 && stats.total_sent > 0 && (
                  <div className="flex gap-3 p-3 bg-orange-50 dark:bg-orange-950/20 rounded-lg border border-orange-200 dark:border-orange-900">
                    <XCircle className="h-5 w-5 text-orange-600 flex-shrink-0 mt-0.5" />
                    <div className="space-y-1">
                      <p className="font-medium text-sm">Low Delivery Rate</p>
                      <p className="text-sm text-muted-foreground">
                        Your delivery rate is {stats.delivery_rate.toFixed(1)}%. Check your email list quality
                        and ensure you're following email best practices to improve deliverability.
                      </p>
                    </div>
                  </div>
                )}

                {/* Open Rate Insights */}
                {stats.open_rate < 20 && stats.total_sent > 100 && (
                  <div className="flex gap-3 p-3 bg-blue-50 dark:bg-blue-950/20 rounded-lg border border-blue-200 dark:border-blue-900">
                    <Eye className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                    <div className="space-y-1">
                      <p className="font-medium text-sm">Low Open Rate</p>
                      <p className="text-sm text-muted-foreground">
                        Your open rate is {stats.open_rate.toFixed(1)}%. Consider A/B testing different
                        subject lines, sender names, and send times to improve engagement.
                      </p>
                    </div>
                  </div>
                )}

                {/* Click Rate Insights */}
                {stats.click_rate < 3 && stats.total_opened > 50 && (
                  <div className="flex gap-3 p-3 bg-purple-50 dark:bg-purple-950/20 rounded-lg border border-purple-200 dark:border-purple-900">
                    <MousePointerClick className="h-5 w-5 text-purple-600 flex-shrink-0 mt-0.5" />
                    <div className="space-y-1">
                      <p className="font-medium text-sm">Low Click Rate</p>
                      <p className="text-sm text-muted-foreground">
                        Your click rate is {stats.click_rate.toFixed(1)}%. Improve your call-to-action buttons,
                        make links more prominent, and ensure content relevance to drive more clicks.
                      </p>
                    </div>
                  </div>
                )}

                {/* Good Performance */}
                {engagementScore >= 70 && (
                  <div className="flex gap-3 p-3 bg-green-50 dark:bg-green-950/20 rounded-lg border border-green-200 dark:border-green-900">
                    <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <div className="space-y-1">
                      <p className="font-medium text-sm">Excellent Performance! 🎉</p>
                      <p className="text-sm text-muted-foreground">
                        This template is performing exceptionally well with a {engagementScore.toFixed(1)}/100
                        engagement score. Keep up the great work!
                      </p>
                    </div>
                  </div>
                )}

                {/* No Data Yet */}
                {stats.total_sent === 0 && (
                  <div className="flex gap-3 p-3 bg-muted rounded-lg">
                    <Mail className="h-5 w-5 text-muted-foreground flex-shrink-0 mt-0.5" />
                    <div className="space-y-1">
                      <p className="font-medium text-sm">No Data Yet</p>
                      <p className="text-sm text-muted-foreground">
                        This template hasn't been used yet. Send some test emails or enable it in your
                        email flows to start collecting analytics data.
                      </p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Comparison with Other Templates */}
            {allAnalytics.length > 1 && (
              <Card>
                <CardHeader>
                  <CardTitle>Comparison with Other Templates</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Your Ranking</span>
                      <span className="font-medium">#{ranking} of {allAnalytics.length}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Average Delivery Rate</span>
                      <span className="font-medium">
                        {(allAnalytics.reduce((sum, a) => sum + a.delivery_rate, 0) / allAnalytics.length).toFixed(1)}%
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Average Open Rate</span>
                      <span className="font-medium">
                        {(allAnalytics.reduce((sum, a) => sum + a.open_rate, 0) / allAnalytics.length).toFixed(1)}%
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Average Click Rate</span>
                      <span className="font-medium">
                        {(allAnalytics.reduce((sum, a) => sum + a.click_rate, 0) / allAnalytics.length).toFixed(1)}%
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};
